import { useState, useEffect, useRef } from "react";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceAssistantTextarea } from "@/components/ui/VoiceAssistantTextarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { AddressAutocomplete, type ParsedAddress } from "@/components/ui/AddressAutocomplete";
import { Building2, FileText, Globe2, MapPin, ExternalLink } from "lucide-react";
import { ClassificationFieldGuide } from "@/components/company/ClassificationFieldGuide";
import { IndustryTagInput } from "@/components/company/IndustryTagInput";
import { SubsectorTagInput } from "@/components/company/SubsectorTagInput";
import { CompanyTypeTagInput } from "@/components/company/CompanyTypeTagInput";
import { CategoryTagInput } from "@/components/company/CategoryTagInput";
import { TypeTagInput } from "@/components/company/TypeTagInput";
import type { Company } from "@/types";
import { editCompanyAddress } from '@/lib/companyAddressEditing';

interface CompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company | null;
  mode: "create" | "edit";
  onSuccess?: (company?: Company) => void;
  presentation?: 'dialog' | 'sheet';
  /**
   * Crea l'azienda gia' come controllata di questa. E' il percorso "nuova
   * filiale": una filiale e' un'azienda con un'azienda madre, quindi si usa
   * questo stesso form invece di averne uno parallelo.
   */
  parentCompanyId?: number | null;
  /** Nome della casa madre, solo per etichettare il form. */
  parentCompanyName?: string | null;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  website: string;
  logo: string;
  full_address: string;
  street: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  latitude: string;
  longitude: string;
  place_id: string;
  industry: string;
  subsectors: string[];
  companyType: string;
  category: string[];
  typeTags: string[];
  description: string;
}

const inputClass = "h-8 rounded-none text-xs";
const labelClass = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";
const sectionClass = "border border-border bg-card";
const sectionHeaderClass = "flex items-center justify-between gap-3 border-b border-border px-3 py-2";
const sectionTitleClass = "flex items-center gap-2 text-xs font-semibold text-foreground";

export default function CompanyModalWorking({ open, onOpenChange, company, mode, onSuccess, presentation = 'dialog', parentCompanyId = null, parentCompanyName = null }: CompanyModalProps) {
  // Creare una filiale e' creare un'azienda con azienda madre: il form e' lo
  // stesso, ma va detto a chiare lettere cosa si sta creando.
  const isSubsidiaryCreation = mode === "create" && Boolean(parentCompanyId);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const emptyForm: FormData = {
    name: "", email: "", phone: "", website: "", logo: "",
    full_address: "", street: "", city: "", province: "", zip: "",
    country: "Italia", latitude: "", longitude: "", place_id: "",
    industry: "", subsectors: [], companyType: "", category: [], typeTags: [], description: "",
  };

  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialFormSnapshot = useRef(JSON.stringify(emptyForm));
  const isDirty = open && JSON.stringify(formData) !== initialFormSnapshot.current;
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard(open, isDirty, onOpenChange);

  useEffect(() => {
    if (company && mode === "edit") {
      const initialForm: FormData = {
        name: company.name || "",
        email: company.email || "",
        phone: company.phone || "",
        website: company.website || "",
        logo: (company as any).logo || "",
        full_address: (company as any).full_address || company.address || "",
        street: (company as any).street || "",
        city: (company as any).city || "",
        province: (company as any).province || "",
        zip: (company as any).zip || "",
        country: (company as any).country || "Italia",
        latitude: (company as any).latitude?.toString() || "",
        longitude: (company as any).longitude?.toString() || "",
        place_id: (company as any).place_id || "",
        industry: company.industry || "",
        subsectors: company.subsectors ?? [],
        companyType: ('company_type' in company ? (company as any).company_type : company.companyType) || "",
        category: Array.isArray(company.category) ? company.category : [],
        typeTags: Array.isArray(company.typeTags) ? company.typeTags : [],
        description: company.description || "",
      };
      initialFormSnapshot.current = JSON.stringify(initialForm);
      setFormData(initialForm);
    } else if (mode === "create") {
      initialFormSnapshot.current = JSON.stringify(emptyForm);
      setFormData(emptyForm);
    }
  }, [company, mode, open]);

  /** Quando l'utente seleziona un'azienda da Google Places, autofilla tutti i campi */
  const handlePlaceSelect = (parsed: ParsedAddress) => {
    setFormData(prev => ({
      ...prev,
      name: parsed.companyName || prev.name,
      full_address: parsed.fullAddress,
      street: parsed.street,
      city: parsed.city,
      province: parsed.province,
      zip: parsed.zip,
      country: parsed.country || "Italia",
      latitude: parsed.latitude?.toString() || "",
      longitude: parsed.longitude?.toString() || "",
      place_id: parsed.placeId,
      // Autofill website e telefono se presenti e campo vuoto
      website: prev.website || parsed.website || "",
      phone: prev.phone || parsed.phone || "",
    }));

    if (parsed.placeTypes.length > 0) {
      void fetch("/api/company-classification/resolve-google-places", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ types: parsed.placeTypes }),
        credentials: "include",
      })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error("classification_failed")))
        .then((payload) => {
          const suggestions = Array.isArray(payload.suggestions) ? payload.suggestions : [];
          setFormData((prev) => {
            const industries = suggestions.filter((item: any) => item.field === "industry").map((item: any) => item.label);
            const subsectors = suggestions.filter((item: any) => item.field === "subsector").map((item: any) => item.label);
            return {
              ...prev,
              industry: prev.industry || industries[0] || "",
              subsectors: Array.from(new Set([...prev.subsectors, ...subsectors])),
            };
          });
        })
        .catch((error) => console.warn("[CompanyModal] Classificazione Places non disponibile:", error));
    }
  };

  const set = (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({ title: "Errore", description: "Il nome dell'azienda è obbligatorio", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);

    try {
      const fullAddressValue = formData.full_address.trim();

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim() || "",
        phone: formData.phone.trim() || "",
        website: formData.website.trim() || "",
        logo: formData.logo.trim() || null,
        address: fullAddressValue,
        full_address: fullAddressValue,
        street: formData.street.trim() || null,
        city: formData.city.trim() || null,
        province: formData.province.trim() || null,
        zip: formData.zip.trim() || null,
        country: formData.country.trim() || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        place_id: formData.place_id || null,
        industry: formData.industry.trim() || "",
        subsectors: formData.subsectors,
        company_type: formData.companyType.trim() || null,
        category: formData.category,
        typeTags: formData.typeTags,
        description: formData.description.trim() || "",
        // Solo in creazione: in modifica l'azienda madre si cambia dalla scheda,
        // e passarla qui sovrascriverebbe una scelta fatta altrove.
        ...(mode === "create" && parentCompanyId ? { parentCompanyId } : {}),
      };

      const url = mode === "create" ? "/api/companies" : `/api/companies/${company!.id}`;
      const method = mode === "create" ? "POST" : "PUT";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText.substring(0, 200));
      }
      const savedCompany = await response.json();

      toast({
        title: "Successo!",
        description: mode === "create" ? "Azienda creata con successo" : "Azienda aggiornata con successo",
      });

      queryClient.removeQueries();
      queryClient.clear();
      setTimeout(() => queryClient.refetchQueries({ type: 'all' }), 50);
      closeAfterSave();
      onSuccess?.(savedCompany);
    } catch (error) {
      toast({
        title: "Errore",
        description: `Errore durante il salvataggio: ${error instanceof Error ? error.message : "Errore sconosciuto"}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className={presentation === 'sheet'
        ? "!left-auto !right-0 !top-[env(safe-area-inset-top)] flex h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] w-full max-w-3xl !translate-x-0 !translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 notion-dialog"
        : "flex max-w-3xl flex-col gap-0 overflow-hidden rounded-none p-0 notion-dialog"}>
        <DialogHeader className="border-b border-border px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-sm font-semibold leading-5 text-foreground notion-title">
              {isSubsidiaryCreation ? "Nuova società controllata" : mode === "create" ? "Crea Azienda" : "Modifica Azienda"}
            </DialogTitle>
            {/* Stesso fix del DealModal (audit 21 lug): nessun modo di
                raggiungere la scheda completa dell'azienda da questo pannello. */}
            {mode === "edit" && company?.id && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 gap-1.5 rounded-none text-xs"
                onClick={() => {
                  if (requestClose(false)) navigate(`/companies/${company.id}`);
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Apri scheda completa
              </Button>
            )}
          </div>
          {isSubsidiaryCreation && (
            <p className="text-xs text-muted-foreground">
              Sara' creata come azienda controllata{parentCompanyName ? ` da ${parentCompanyName}` : ""}, con tutte le
              capacita' di un'azienda.
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
            <section className={sectionClass}>
              <div className={sectionHeaderClass}>
                <div>
                  <p className={sectionTitleClass}>
                    <Building2 className="h-3.5 w-3.5" />
                    Identita'
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Nome, settore e canali principali.</p>
                </div>
              </div>
              <div className="grid gap-3 p-3 sm:grid-cols-2">
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="name" className={labelClass}>Nome Azienda *</Label>
                  <AddressAutocomplete
                    id="name"
                    mode="establishment"
                    value={formData.name}
                    onChange={v => setFormData(prev => ({ ...prev, name: v }))}
                    onAddressSelect={handlePlaceSelect}
                    placeholder="Digita il nome dell'azienda..."
                    className={inputClass}
                  />
                </div>

                <div className="space-y-1">
                  <ClassificationFieldGuide field="industry" htmlFor="industry" labelClassName={labelClass} />
                  <IndustryTagInput
                    value={formData.industry ? [formData.industry] : []}
                    onChange={(tags) => setFormData(prev => ({ ...prev, industry: tags[0] || "" }))}
                    placeholder="Cerca settore..."
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="website" className={labelClass}>Sito Web</Label>
                  <Input id="website" className={inputClass} value={formData.website} onChange={set("website")} placeholder="https://www.azienda.it" />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="email" className={labelClass}>Email</Label>
                  <Input id="email" className={inputClass} type="email" value={formData.email} onChange={set("email")} placeholder="email@azienda.it" />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="phone" className={labelClass}>Telefono</Label>
                  <Input id="phone" className={inputClass} value={formData.phone} onChange={set("phone")} placeholder="+39 02 1234567" />
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="logo" className={labelClass}>URL logo</Label>
                  <div className="flex gap-2">
                    <Input id="logo" className={`${inputClass} min-w-0`} type="url" value={formData.logo} onChange={set("logo")} placeholder="https://azienda.it/logo.png" />
                    <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 rounded-none text-xs" disabled={!formData.logo} onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}>Rimuovi logo</Button>
                  </div>
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <ClassificationFieldGuide field="subsector" labelClassName={labelClass} />
                  <SubsectorTagInput
                    value={formData.subsectors}
                    onChange={(tags) => setFormData(prev => ({ ...prev, subsectors: tags }))}
                    placeholder="Aggiungi sottosettore..."
                  />
                </div>

                <div className="space-y-1">
                  <ClassificationFieldGuide field="companyType" labelClassName={labelClass} />
                  <CompanyTypeTagInput
                    value={formData.companyType ? [formData.companyType] : []}
                    onChange={(tags) => setFormData(prev => ({ ...prev, companyType: tags[0] || "" }))}
                    placeholder="Aggiungi tipo azienda..."
                  />
                </div>

                <div className="space-y-1">
                  <ClassificationFieldGuide field="category" labelClassName={labelClass} />
                  <CategoryTagInput
                    value={formData.category}
                    onChange={(tags) => setFormData(prev => ({ ...prev, category: tags }))}
                    placeholder="Aggiungi categoria..."
                  />
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <ClassificationFieldGuide field="typeTags" labelClassName={labelClass} />
                  <TypeTagInput
                    value={formData.typeTags}
                    onChange={(tags) => setFormData(prev => ({ ...prev, typeTags: tags }))}
                    placeholder="Aggiungi tag tipologia..."
                  />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <div className={sectionHeaderClass}>
                <div>
                  <p className={sectionTitleClass}>
                    <MapPin className="h-3.5 w-3.5" />
                    Indirizzo
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Sede, citta' e coordinate da Places quando disponibili.</p>
                </div>
                {(formData.latitude || formData.longitude) && (
                  <span className="text-xs font-medium text-muted-foreground">
                    Coordinate {formData.latitude}, {formData.longitude}
                  </span>
                )}
              </div>
              <div className="grid gap-3 p-3 sm:grid-cols-4">
                <div className="space-y-1 sm:col-span-4">
                  <Label htmlFor="street" className={labelClass}>Via / Indirizzo</Label>
                  <AddressAutocomplete
                    id="street"
                    mode="address"
                    value={formData.street || formData.full_address}
                    onChange={v => setFormData(prev => editCompanyAddress(prev, 'street', v))}
                    onAddressSelect={parsed => setFormData(prev => ({
                      ...prev,
                      full_address: parsed.fullAddress,
                      street: parsed.street,
                      city: parsed.city,
                      province: parsed.province,
                      zip: parsed.zip,
                      country: parsed.country || '',
                      latitude: parsed.latitude?.toString() ?? '',
                      longitude: parsed.longitude?.toString() ?? '',
                      place_id: parsed.placeId || '',
                    }))}
                    placeholder="Via Roma 1"
                    className={inputClass}
                  />
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="city" className={labelClass}>Citta'</Label>
                  <AddressAutocomplete id="city" mode="city" className={inputClass} value={formData.city} onChange={city => setFormData(prev => editCompanyAddress(prev, 'city', city))} onAddressSelect={place => setFormData(prev => editCompanyAddress({ ...prev, country: place.country || prev.country }, 'city', place.city))} placeholder="Milano" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="province" className={labelClass}>Prov.</Label>
                  <Input id="province" className={inputClass} value={formData.province} onChange={e => setFormData(prev => editCompanyAddress(prev, 'province', e.target.value))} placeholder="MI" maxLength={5} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="zip" className={labelClass}>CAP</Label>
                  <Input id="zip" className={inputClass} value={formData.zip} onChange={e => setFormData(prev => editCompanyAddress(prev, 'zip', e.target.value))} placeholder="20121" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="country" className={labelClass}>Paese</Label>
                  <AddressAutocomplete id="country" mode="country" className={inputClass} value={formData.country} onChange={country => setFormData(prev => editCompanyAddress(prev, 'country', country))} placeholder="Italia" />
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <div className={sectionHeaderClass}>
                <div>
                  <p className={sectionTitleClass}>
                    <FileText className="h-3.5 w-3.5" />
                    Memoria azienda
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Descrizione breve e contesto operativo.</p>
                </div>
                <Globe2 className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="space-y-1 p-3">
                <Label htmlFor="description" className={labelClass}>Descrizione</Label>
                <VoiceAssistantTextarea
                  id="description"
                  value={formData.description}
                  onValueChange={(value) => setFormData((current) => ({ ...current, description: value }))}
                  fieldLabel="Descrizione e memoria Azienda"
                  martinaContext={{ terminalType: "entity", entityType: "company", entityId: company?.id ? String(company.id) : undefined, route: company?.id ? `/companies/${company.id}` : "/companies" }}
                  placeholder="Descrizione dell'azienda..."
                  className="min-h-[72px] rounded-none text-xs"
                  rows={3}
                />
              </div>
            </section>
          </div>

          <DialogFooter className="border-t border-border px-4 py-3">
            <Button type="button" variant="outline" onClick={() => requestClose(false)} disabled={isSubmitting}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvataggio..." : isSubsidiaryCreation ? "Crea società controllata" : mode === "create" ? "Crea Azienda" : "Salva Modifiche"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
